/**
 * Utility functions for converting image URLs to public-accessible formats
 * Used primarily for social sharing where private Supabase URLs need to be converted
 * to public image-server URLs
 */

/**
 * Check if a URL is a private Supabase storage URL
 */
export function isPrivateSupabaseUrl(url: string): boolean {
  return url && url.includes('.supabase.co/storage/');
}

/**
 * Extract the filename from a Supabase URL or any URL
 */
function extractFilename(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    
    // For Supabase URLs, extract the actual filename from the path
    // Format is usually /storage/v1/object/public/bucket/filename
    const segments = pathname.split('/');
    return segments[segments.length - 1] || '';
  } catch (error) {
    console.error('Error extracting filename from URL:', url, error);
    return '';
  }
}

/**
 * Convert a private Supabase URL to a public image-server URL
 * This allows images to be accessed publicly for social sharing
 */
export function convertToImageServerUrl(supabaseUrl: string, bookId?: string, pageNumber?: number): string {
  try {
    // If it's not a Supabase URL, return as-is
    if (!isPrivateSupabaseUrl(supabaseUrl)) {
      return supabaseUrl;
    }
    
    const urlObj = new URL(supabaseUrl);
    const pathname = urlObj.pathname;
    
    // Extract storage path components
    // Format can be:
    // - /storage/v1/object/public/[bucket]/[...path]/filename
    // - /storage/v1/object/sign/[bucket]/[...path]/filename (for signed URLs)
    const match = pathname.match(/\/storage\/v1\/object\/(public|sign)\/(.+)$/);
    
    if (!match || !match[2]) {
      console.error('Could not parse Supabase storage path:', pathname);
      return supabaseUrl; // Return original URL as fallback
    }
    
    // The full path including bucket (match[2] skips the public/sign part)
    const fullStoragePath = match[2];
    
    // Remove query parameters from the path if it's a signed URL
    const cleanPath = fullStoragePath.split('?')[0];
    
    // Return image-server URL that preserves the full path
    return `/api/image-server/${cleanPath}`;
  } catch (error) {
    console.error('Error converting Supabase URL to image-server URL:', error);
    return supabaseUrl; // Return original URL as fallback
  }
}

/**
 * Convert all image URLs in a book's pages to public image-server URLs
 * This is useful when preparing book data for social sharing
 */
export function convertBookImagesToPublicUrls(book: any): any {
  if (!book || !book.pages) {
    return book;
  }
  
  return {
    ...book,
    pages: book.pages.map((page: any) => ({
      ...page,
      imageUrl: page.imageUrl ? convertToImageServerUrl(page.imageUrl, book.id, page.pageNumber) : page.imageUrl
    }))
  };
}

/**
 * Get a public URL for any image URL, converting private ones as needed
 * This is the main function to use when displaying images in social sharing contexts
 */
export function getPublicImageUrl(imageUrl: string, bookId?: string, pageNumber?: number): string {
  if (!imageUrl) {
    return '';
  }
  
  // If it's already a public URL (starts with http/https and not Supabase), return as-is
  if ((imageUrl.startsWith('http') || imageUrl.startsWith('https')) && !isPrivateSupabaseUrl(imageUrl)) {
    return imageUrl;
  }
  
  // If it's already an image-server URL, return as-is
  if (imageUrl.startsWith('/api/image-server/')) {
    return imageUrl;
  }
  
  // Convert private Supabase URLs to image-server URLs
  if (isPrivateSupabaseUrl(imageUrl)) {
    return convertToImageServerUrl(imageUrl, bookId, pageNumber);
  }
  
  // If it's a relative URL, assume it's already correct
  if (imageUrl.startsWith('/')) {
    return imageUrl;
  }
  
  // For any other case, return as-is
  return imageUrl;
}
